// Backlog 187 — structured deploy-error renderer.
//
// The API computes the user-language translation server-side via
// `translateDeployError` and persists it on the deploy_event row. By the
// time the CLI sees it, the hard work is done: the renderer just picks
// the right slices of the structured payload and lays them out.
//
// Layout (default, no --verbose):
//
//   <chalk.red headline derived from message_user>
//
//     <ordered remediation lines from suggested_fixes>
//     <first command, indented + chalk.cyan>
//
//     Trace <request_id>. Run `grid follow <request_id> --verbose`
//     for the technical detail.
//
// Layout (verbose):
//   …above, plus a "Technical detail:" block with the message_technical
//   and the failed_step.
//
// §23 voice rules apply to anything this renderer writes itself; the
// translated content was already vetted at write time (see
// `packages/api/src/services/deploy-error-translate.ts`).

import chalk from 'chalk';

import type { BuildLogExcerpt } from '../../../shared/src/deploy-event-error.js';
import type { DeployTrace, DeployTraceError } from '../services/plug-poll.js';

/**
 * Backlog 292 — how many trailing build-log lines the default (non-verbose)
 * surface shows. The failing lines sit at the tail of a build log, so the
 * last N lines carry the actionable error. `--verbose` prints the full tail.
 */
const DEFAULT_BUILD_LOG_TAIL_LINES = 12;

export interface RenderStructuredOpts {
  /** Entity slug. Inlined where the renderer needs to name the entity. */
  entitySlug: string;
  /** When true, append the technical detail block. */
  verbose?: boolean;
}

/**
 * Type guard. The legacy CLI shape was `{ code, message }`; the new shape
 * carries `schema_version`. The renderer dispatches on the presence of
 * `schema_version` so old + new payloads can coexist on the wire.
 */
export function hasStructuredDeployError(trace: DeployTrace): boolean {
  const e = trace.error as unknown;
  return (
    e !== null &&
    typeof e === 'object' &&
    (e as { schema_version?: unknown }).schema_version === 1
  );
}

export function renderStructuredDeployError(
  err: DeployTraceError,
  opts: RenderStructuredOpts,
): string {
  const structured = err as DeployTraceError & {
    schema_version?: number;
    message_user?: string;
    message_technical?: string;
    category?: string;
    severity?: string;
    suggested_fixes?: Array<{ summary: string; command?: string; for: string }>;
    request_id?: string;
    failed_step?: string;
    // Backlog 219 — optional plug-fix agent suggestion. Renderers that
    // don't know about this field MUST ignore (per contract §4 forward-
    // compat); when present, surface the message as a last "AI suggests"
    // line so the user sees a concrete next step.
    ai_suggestion?: { message?: string };
    // Backlog 271/292 — server-captured Cloud Build log tail + deep link,
    // typed against the shared `BuildLogExcerpt` contract. Surfaced inline
    // below so a failed `plug`/`follow` shows the actual build error (e.g.
    // the TypeScript error that failed the build), not just the generic
    // "check the build logs" headline. Lifted onto the cast so the field is
    // accessible on the legacy (non-structured) branch of DeployTraceError.
    build_log_excerpt?: BuildLogExcerpt;
  };
  const lines: string[] = [];

  const headline = structured.message_user ?? structured.message ?? 'Deploy failed.';
  lines.push(chalk.red(headline));
  lines.push('');

  const fixes = structured.suggested_fixes ?? [];
  if (fixes.length > 0) {
    for (const fix of fixes) {
      lines.push(`  ${fix.summary}`);
      if (fix.command) {
        lines.push(`    ${chalk.cyan(fix.command)}`);
      }
    }
    lines.push('');
  }

  // Backlog 292 — the build-log excerpt is the most actionable content on a
  // cloud_build failure, so it shows by default (not gated behind --verbose).
  // Non-verbose prints the tail end (the failing lines are last); verbose
  // prints the whole captured tail. When the server captured an id but no
  // text, fall back to the console link so the user still has a path forward.
  const excerpt = structured.build_log_excerpt;
  const excerptText = typeof excerpt?.text === 'string' ? excerpt.text : '';
  if (excerptText.length > 0) {
    const allLines = excerptText.replace(/\n+$/, '').split('\n');
    const shown = opts.verbose
      ? allLines
      : allLines.slice(-DEFAULT_BUILD_LOG_TAIL_LINES);
    const truncated = shown.length < allLines.length;
    lines.push(chalk.dim(`  Build log${truncated ? ' (last lines)' : ''}:`));
    for (const l of shown) lines.push(chalk.dim(`    ${l}`));
    if (excerpt?.console_url) {
      lines.push(chalk.dim(`    full log: ${excerpt.console_url}`));
    }
    lines.push('');
  } else if (excerpt?.console_url) {
    lines.push(chalk.dim(`  Build log: ${excerpt.console_url}`));
    lines.push('');
  }

  if (structured.category === 'platform_bug') {
    lines.push(
      chalk.dim(
        `  This looks like a platform issue. The platform team can help — report request_id ${structured.request_id ?? '<unknown>'}.`,
      ),
    );
    lines.push('');
  }

  const reqId = structured.request_id ?? structured.failed_step ?? 'unknown';
  lines.push(
    chalk.dim(
      `  Trace ${reqId}. Run \`grid follow ${reqId} --verbose\` for the full step log.`,
    ),
  );

  // Backlog 219 — surface the plug-fix agent's suggestion as the last line
  // when one was persisted. The agent's `message` field is already §23-vetted
  // server-side (parser rejects K8s jargon + enforces the voice prefix); the
  // CLI just prefixes it so the line reads `AI suggests: <message>`.
  const aiMessage = typeof structured.ai_suggestion?.message === 'string'
    ? structured.ai_suggestion.message.trim()
    : '';
  if (aiMessage.length > 0) {
    lines.push('');
    lines.push(`  ${chalk.bold('AI suggests:')} ${aiMessage}`);
  }

  if (opts.verbose) {
    lines.push('');
    lines.push(chalk.dim('  Technical detail:'));
    if (structured.failed_step) {
      lines.push(chalk.dim(`    Failed step: ${structured.failed_step}`));
    }
    if (structured.message_technical) {
      lines.push(chalk.dim(`    ${structured.message_technical}`));
    }
  }

  return lines.join('\n');
}
